/**
 * Payroll Export Tab - Export payroll data to QuickBooks, ADP, Paychex
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Download,
  FileSpreadsheet,
  Calculator,
  Building2,
  Cloud,
  Monitor,
  Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PayrollExportTab() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState("");
  const [summary, setSummary] = useState(null);

  const fetchSummary = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select start and end dates");
      return;
    }

    try {
      setLoading("summary");
      const response = await axios.get(`${API}/payroll-export/summary`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setSummary(response.data);
    } catch (error) {
      toast.error("Failed to fetch summary");
    } finally {
      setLoading("");
    }
  };

  const handleExport = async (type) => {
    if (!startDate || !endDate) {
      toast.error("Please select start and end dates");
      return;
    }

    try {
      setLoading(type);
      
      let endpoint = "";
      let filename = "";
      
      switch (type) {
        case "quickbooks_online":
          endpoint = "/payroll-export/quickbooks/online";
          filename = `quickbooks_online_${startDate}_${endDate}.csv`;
          break;
        case "quickbooks_desktop":
          endpoint = "/payroll-export/quickbooks/desktop";
          filename = `quickbooks_desktop_${startDate}_${endDate}.iif`;
          break;
        case "adp":
          endpoint = "/payroll-export/adp";
          filename = `adp_payroll_${startDate}_${endDate}.csv`;
          break;
        case "paychex":
          endpoint = "/payroll-export/paychex";
          filename = `paychex_payroll_${startDate}_${endDate}.csv`;
          break;
        default:
          return;
      }

      const response = await axios.get(`${API}${endpoint}`, {
        params: { start_date: startDate, end_date: endDate },
        responseType: "blob"
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${type.replace("_", " ").toUpperCase()} export downloaded`);
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    } finally {
      setLoading("");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-slate-800">Payroll Export</h3>
        <p className="text-sm text-slate-500">Export payroll data to QuickBooks, ADP, or Paychex</p>
      </div>

      {/* Date Range Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Select Pay Period</CardTitle>
          <CardDescription>Choose the date range for payroll export</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <Button 
              onClick={fetchSummary}
              disabled={loading === "summary"}
              variant="outline"
            >
              {loading === "summary" ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Calculator className="w-4 h-4 mr-2" />
              )}
              Preview Summary
            </Button>
          </div>

          {/* Summary Preview */}
          {summary && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-800 mb-3">Export Preview</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Records</p>
                  <p className="font-semibold text-lg">{summary.record_count}</p>
                </div>
                <div>
                  <p className="text-slate-500">Regular Hours</p>
                  <p className="font-semibold text-lg">{summary.total_regular_hours}</p>
                </div>
                <div>
                  <p className="text-slate-500">Overtime Hours</p>
                  <p className="font-semibold text-lg">{summary.total_overtime_hours}</p>
                </div>
                <div>
                  <p className="text-slate-500">Gross Pay</p>
                  <p className="font-semibold text-lg text-emerald-600">${summary.total_gross_pay?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Deductions</p>
                  <p className="font-semibold text-lg text-red-600">${summary.total_deductions?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Net Pay</p>
                  <p className="font-semibold text-lg text-blue-600">${summary.total_net_pay?.toFixed(2)}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export Options */}
      <div className="grid grid-cols-2 gap-4">
        {/* QuickBooks Online */}
        <Card className="border-2 hover:border-emerald-300 transition-colors">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Cloud className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-base">QuickBooks Online</CardTitle>
                <CardDescription>Cloud-based QuickBooks</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">
              Export as CSV file compatible with QuickBooks Online Time Activity import.
            </p>
            <Button
              onClick={() => handleExport("quickbooks_online")}
              disabled={!!loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {loading === "quickbooks_online" ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export for QB Online
            </Button>
          </CardContent>
        </Card>

        {/* QuickBooks Desktop */}
        <Card className="border-2 hover:border-blue-300 transition-colors">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Monitor className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base">QuickBooks Desktop</CardTitle>
                <CardDescription>Desktop application</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">
              Export as IIF file format for QuickBooks Desktop import.
            </p>
            <Button
              onClick={() => handleExport("quickbooks_desktop")}
              disabled={!!loading}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {loading === "quickbooks_desktop" ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export for QB Desktop
            </Button>
          </CardContent>
        </Card>

        {/* ADP */}
        <Card className="border-2 hover:border-red-300 transition-colors">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Building2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-base">ADP Payroll</CardTitle>
                <CardDescription>ADP Workforce Now</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">
              Export in ADP standard payroll import format (CSV).
            </p>
            <Button
              onClick={() => handleExport("adp")}
              disabled={!!loading}
              className="w-full bg-red-600 hover:bg-red-700"
            >
              {loading === "adp" ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export for ADP
            </Button>
          </CardContent>
        </Card>

        {/* Paychex */}
        <Card className="border-2 hover:border-orange-300 transition-colors">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <FileSpreadsheet className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-base">Paychex</CardTitle>
                <CardDescription>Paychex Flex</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">
              Export in Paychex standard payroll import format (CSV).
            </p>
            <Button
              onClick={() => handleExport("paychex")}
              disabled={!!loading}
              className="w-full bg-orange-600 hover:bg-orange-700"
            >
              {loading === "paychex" ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export for Paychex
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Help Text */}
      <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-600">
        <h4 className="font-medium text-slate-800 mb-2">Export Instructions</h4>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>QuickBooks Online:</strong> Go to Payroll → Time Entries → Import Time</li>
          <li><strong>QuickBooks Desktop:</strong> File → Utilities → Import → IIF Files</li>
          <li><strong>ADP:</strong> Upload via ADP Workforce Now import tool</li>
          <li><strong>Paychex:</strong> Upload via Paychex Flex file import</li>
        </ul>
      </div>
    </div>
  );
}
